import React, { useState, useRef, useEffect } from "react";
import { CircleStop, Mic, Volume2, VolumeOff } from "lucide-react";
import AiChatWidget from "../Components/AiChatWidget";
import storage from "../Assets/js/Global";

const SAMPLE_MESSAGES = [
  {
    id: 1,
    role: "assistant",
    text: "Hello! I'm your AI health assistant. How can I help you today?",
  },
  {
    id: 2,
    role: "user",
    text: "What does my latest blood pressure reading mean?",
  },
  {
    id: 3,
    role: "assistant",
    text: "Blood pressure is measured in two numbers: systolic (top) and diastolic (bottom). A normal reading is below 120/80 mmHg. If your reading is elevated, it's best to consult your doctor. Would you like to share your specific reading so I can give more tailored insights?",
  },
];

const SpeechRecognition =
  window.SpeechRecognition || window.webkitSpeechRecognition;

const AskAI = () => {
  const [messages, setMessages] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const finalTranscriptRef = useRef("");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    let intervalId;

    const initChat = (validToken) => {
      const userId = storage.get("chatVendorUserKey") || "";
      const projectId = storage.get("chatVendorProjectKey") || "tzdta6y";
      console.log("userId", userId);
      console.log("projectId", projectId);
      console.log("validToken", validToken);
      const chatWidget = new AiChatWidget({
        promptInputId: "ask-ai-prompt-input",
        sendButtonId: "ask-ai-send-btn",
        chatContainerId: "ask-ai-chat-container",
        token: validToken,
        userId: userId,
        projectId: projectId,
        language: storage.get("selectedLanguage") || "en",
        apiUrl: "https://chatapi.globalhealthiq.ai"
      });
      console.log("chatWidget", chatWidget);

      chatWidget.init();
    };

    const token = storage.get("accessToken");
    if (token) {
      initChat(token);
    } else {
      intervalId = setInterval(() => {
        const checkToken = storage.get("accessToken");
        if (checkToken) {
          clearInterval(intervalId);
          initChat(checkToken);
        }
      }, 500);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const autoResize = () => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
  };

  const handleMicClick = async () => {
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      setSpeakingMessageId(null);
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasMic = devices.some((device) => device.kind === "audioinput");

    if (!hasMic) {
      setErrorMessage("No microphone detected on this device.");
      return;
    }
    if (!SpeechRecognition) {
      alert("Speech Recognition API is not supported in this browser.");
      return;
    }

    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;

      recognition.lang = "en-US";
      recognition.interimResults = true;
      recognition.continuous = true;

      finalTranscriptRef.current = "";
      if (textareaRef.current) textareaRef.current.value = "";

      recognition.onstart = () => setIsRecording(true);
      recognition.onresult = (event) => {
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (!event.results[i].isFinal) {
            interimTranscript += event.results[i][0].transcript;
          } else {
            finalTranscriptRef.current += event.results[i][0].transcript;
          }
        }

        const fullText = finalTranscriptRef.current + interimTranscript;
        if (textareaRef.current) {
          textareaRef.current.value = fullText;
          autoResize();

          const btn = document.getElementById("ask-ai-send-btn");
          if (btn) {
            if (fullText.trim()) {
              btn.classList.add("ask-ai-send-btn--active");
              btn.disabled = false;
            } else {
              btn.classList.remove("ask-ai-send-btn--active");
              btn.disabled = true;
            }
          }
        }
      };

      recognition.onerror = (event) => {
        if (event.error === "no-speech") {
          console.warn("No speech detected");
          return;
        }

        console.error("Speech recognition error:", event.error);
        setIsRecording(false);

        if (event.error === "not-allowed") {
          setErrorMessage(
            "Unable to access microphone. Please connect a microphone.",
          );
        }
      };

      recognition.onend = () => setIsRecording(false);

      recognition.start();
    } catch (err) {
      console.error("Microphone access denied:", err);
      setErrorMessage(
        "Unable to access microphone. Please connect a microphone.",
      );
      setIsRecording(false);
    }
  };

  const speakText = (text) => {
    if (!window.speechSynthesis) return;

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    const msg = new SpeechSynthesisUtterance(text);
    msg.lang = "en-US";
    msg.rate = 1;
    msg.pitch = 1;
    window.speechSynthesis.speak(msg);
  };

  return (
    <div className="content-part">
      {/* <div className="" style={{ marginBottom: "15px" }}> */}
      {errorMessage && <div className="ask-ai-toast">{errorMessage}</div>}
      <div className="ask-ai-messages" id="ask-ai-chat-container">
        <div className="ask-ai-inner">
        </div>
        {/* {messages.length === 0 && (
            <div className="ask-ai-empty">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#b0c4cc"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <p className="text-muted mt-3 fs-14px">
                Ask me anything about Healthy 4life.
              </p>
            </div>
          )} */}

        {messages.map((msg) => (
          <div key={msg.id} className="ask-ai-message-wrapper">
            <div
              className={`ask-ai-row ${msg.role === "user" ? "ask-ai-row--user" : "ask-ai-row--bot"
                }`}
            >
              <div
                className={`ask-ai-bubble ${msg.role === "user"
                  ? "ask-ai-bubble--user"
                  : "ask-ai-bubble--bot"
                  }`}
              >
                {msg.text}
              </div>
            </div>

            {msg.role === "assistant" && (
              <div className="ask-ai-volume-wrapper">
                <div
                  className="cursor-pointer"
                  onClick={() => {
                    if (speakingMessageId === msg.id) {
                      window.speechSynthesis.cancel();
                      setSpeakingMessageId(null);
                    } else {
                      speakText(msg.text);
                      setSpeakingMessageId(msg.id);
                    }
                  }}
                >
                  {speakingMessageId === msg.id ? (
                    <VolumeOff size={16} />
                  ) : (
                    <Volume2 size={16} />
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      <div className="ask-ai-inputbar">
        <div className="ask-ai-inputbox">
          {/* <div
              className="ask-ai-mic-slot cursor-pointer"
              onClick={handleMicClick}
            >
              {!isRecording ? (
                <Mic size={20} />
              ) : (
                <CircleStop size={20} color="#ff4d4f" />
              )}
            </div> */}
          <textarea
            id="ask-ai-prompt-input"
            ref={textareaRef}
            className="ask-ai-textarea"
            placeholder="Message AI assistant..."
            onChange={(e) => {
              autoResize();
              const btn = document.getElementById("ask-ai-send-btn");
              if (btn) {
                if (e.target.value.trim()) {
                  btn.classList.add("ask-ai-send-btn--active");
                  btn.disabled = false;
                } else {
                  btn.classList.remove("ask-ai-send-btn--active");
                  btn.disabled = true;
                }
              }
            }}
            rows={1}
          />
          <button
            id="ask-ai-send-btn"
            className="ask-ai-send-btn"
            disabled={true}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
        <p className="ask-ai-hint">
          AI responses may not always be accurate. Please verify important information independently.
        </p>
      </div>
      {/* </div> */}
    </div>
  );
};

export default AskAI;
